import { IconNames } from '@blueprintjs/icons';
import { BenefitTypeDisplayTextMap } from '@strata/constants';
import { DateFormats, formatDate } from '@strata/helper-functions';
import { ReferralChatIcon } from '@strata/react-chat';
import { FormPageContainer } from '@strata/react-form-header-and-content';
import {
    ColumnDef,
    FilterCategory,
    GridWithFilters,
    ProductCell,
    SecondaryData,
} from '@strata/react-grid';
import { PatientInfoList } from '@strata/react-label-value-pairs';
import { Icon } from '@strata/react-status-icons';
import { ComponentProps, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { AppRoutes } from 'services/routes';
import { ReferralConversationCounts } from 'services/userSupportHelpers';
import { useAppSelector } from 'store';
import { ExpiredReferral, useGetExpiredReferralsQuery } from 'store/api/performanceApi';
import { useLoggedInSupplierName } from 'store/auth/authThunks';
import { Grids } from 'store/grids/gridsSlice';
import { useGridState } from 'store/grids/gridThunks';

const initialSortProperties = [{ id: 'referralExpiration', desc: true }];

const customSearchValueHandlers: ComponentProps<
    typeof GridWithFilters<ExpiredReferral>
>['customSearchValueHandlers'] = {
    referralExpiration: (value) => formatDate(value, DateFormats.DATE_TIME),
};

const filterCategories: FilterCategory<ExpiredReferral>[] = [
    { key: 'segmentName', title: 'Health Plan' },
    { key: 'product', title: 'Product' },
    {
        key: 'benefit',
        title: 'Benefit',
        valueDisplayTextMap: BenefitTypeDisplayTextMap,
    },
];

const columns = ({
    referralConversationCounts,
}: {
    referralConversationCounts?: ReferralConversationCounts;
}): ColumnDef<ExpiredReferral>[] => [
    {
        accessorKey: 'product',
        header: 'Product',
        size: 140,
        cell: ({ row, getValue }) => (
            <ProductCell product={getValue()} isBrand={row.original.isBrand} primary />
        ),
    },
    {
        accessorKey: 'patient',
        header: 'Patient',
        size: 100,
    },
    {
        accessorKey: 'memberId',
        header: 'Member ID',
        size: 100,
    },
    {
        accessorKey: 'segmentName',
        header: 'Health Plan',
        size: 100,
    },
    {
        accessorKey: 'valueCases',
        header: 'Attributes',
        size: 80,
        enableSorting: false,
        cell: ({ getValue, row }) => (
            <PatientInfoList
                values={[
                    ...(getValue() ?? []),
                    ...(row.original.hasCopayAssistance
                        ? [{ strat_rule_name: PatientInfoList.Types.COPAY }]
                        : []),
                ]}
                incentiveTotal={row.original.incentiveTotal}
                iconsOnly
            />
        ),
    },
    {
        accessorKey: 'referralId',
        header: () => <Icon icon={IconNames.CHAT} />,
        enableSorting: false,
        size: 45,
        cell: ({ getValue }) => (
            <ReferralChatIcon
                openConversations={referralConversationCounts?.[getValue()]?.open}
                unreadMessages={referralConversationCounts?.[getValue()]?.unread}
            />
        ),
    },
    {
        accessorKey: 'referralExpiration',
        header: 'Expiration Date',
        size: 80,
        cell: ({ getValue }) => (
            <SecondaryData data={formatDate(getValue(), DateFormats.DATE_TIME)} />
        ),
    },
];

export const ExpiredReferralListPage = () => {
    const navigate = useNavigate();
    const loggedInSupplierName = useLoggedInSupplierName();

    const { data, isLoading, refetch } = useGetExpiredReferralsQuery();
    useEffect(() => {
        (() => refetch())();
    }, [refetch]);

    const referralConversationCounts = useAppSelector(
        (state) => state.userSupport.referralConversationCounts,
    );

    const {
        sort,
        search,
        activeFilters,
        filterPaneWidth,
        updateGridSort,
        updateGridSearch,
        updateGridActiveFilters,
        updateFilterPaneWidth,
    } = useGridState(Grids.EXPIRED_REFERRALS, initialSortProperties);

    return (
        <FormPageContainer>
            <GridWithFilters
                title="Expired Referrals"
                secondaryTitle={loggedInSupplierName}
                data={data}
                isLoading={isLoading}
                filterCategories={filterCategories}
                activeFilters={activeFilters}
                onFilter={updateGridActiveFilters}
                filterPaneRenderWidth={filterPaneWidth}
                onFilterPaneResize={updateFilterPaneWidth}
                sortProperties={sort}
                onSort={updateGridSort}
                enableSearch
                customSearchValueHandlers={customSearchValueHandlers}
                searchText={search}
                onSearch={updateGridSearch}
                onRowClick={(rowData) => navigate(AppRoutes.wonReferralDetails(rowData.referralId))}
                columns={columns({ referralConversationCounts })}
            />
        </FormPageContainer>
    );
};
